// TableOfContents.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Chip, 
  useTheme, 
  IconButton, 
  Tooltip, 
  Collapse, 
  Badge,
  InputAdornment,
  TextField,
  Drawer,
  Fab,
  useMediaQuery,
  LinearProgress
} from '@mui/material';
import { 
  ListAlt as ListAltIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const TableOfContents = ({ 
  sections, 
  maxHeight = '70vh',
  showProgress = true,
  allowCollapse = true,
  showReadingTime = true,
  showSearch = true,
  showBookmarks = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeSection, setActiveSection] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bookmarkedSections, setBookmarkedSections] = useState([]);
  const [readSections, setReadSections] = useState([]);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  
  const tocRef = useRef(null);
  const activeSectionRef = useRef(null);

  // Initialize expanded sections
  useEffect(() => {
    const initialExpanded = {};
    sections.forEach(section => {
      initialExpanded[section.id] = true;
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          initialExpanded[subsection.id] = false;
          if (subsection.items) {
            subsection.items.forEach(item => {
              initialExpanded[item.id] = false;
            });
          }
        });
      }
    });
    setExpandedSections(initialExpanded);
  }, [sections]);

  // Track scroll position to highlight active section and update progress
  useEffect(() => {
    // Fix for the NaN% reading progress issue
const handleScroll = () => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Show/hide floating button based on scroll position
    setShowFloatingButton(scrollPosition > 300);
    
    // Calculate overall reading progress
    // Fix for NaN% - ensure we don't divide by zero and handle edge cases
    let progress = 0;
    if (documentHeight > windowHeight) {
      progress = Math.min(
        Math.round((scrollPosition / (documentHeight - windowHeight)) * 100),
        100
      );
      // Ensure progress is a valid number
      if (isNaN(progress) || !isFinite(progress)) {
        progress = 0;
      }
    }
    
    setOverallProgress(progress);
          
      // Find all sections and their positions
      const allSectionElements = [];
      
      // Collect all section elements
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          allSectionElements.push({ id: section.id, element });
        }
        
        if (section.subsections) {
          section.subsections.forEach(subsection => {
            const subElement = document.getElementById(subsection.id);
            if (subElement) {
              allSectionElements.push({ id: subsection.id, element: subElement });
            }
            
            if (subsection.items) {
              subsection.items.forEach(item => {
                const itemElement = document.getElementById(item.id);
                if (itemElement) {
                  allSectionElements.push({ id: item.id, element: itemElement });
                }
              });
            }
          });
        }
      });
      
      // Sort elements by their position on the page
      allSectionElements.sort((a, b) => {
        return a.element.getBoundingClientRect().top - b.element.getBoundingClientRect().top;
      });
      
      // Find the first section that is currently in view or just above
      let currentSection = null;
      for (const { id, element } of allSectionElements) {
        const { top } = element.getBoundingClientRect();
        if (top <= 150) {
          currentSection = id;
        } else {
          break;
        }
      }
      
      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
        
        // Mark section as read
        setReadSections(prev => {
          if (!prev.includes(currentSection)) {
            return [...prev, currentSection];
          }
          return prev;
        });
        
        // Auto-expand parent sections of active section
        let sectionToExpand = null;
        
        // Check if the active section is a subsection or sub-subsection
        sections.forEach(section => {
          if (section.subsections) {
            section.subsections.forEach(subsection => {
              if (subsection.id === currentSection) {
                sectionToExpand = section.id;
              }
              
              if (subsection.items) {
                subsection.items.forEach(item => {
                  if (item.id === currentSection) {
                    sectionToExpand = subsection.id;
                    
                    // Also expand the top-level section
                    setExpandedSections(prev => ({
                      ...prev,
                      [section.id]: true
                    }));
                  }
                });
              }
            });
          }
        });
        
        if (sectionToExpand) {
          setExpandedSections(prev => ({
            ...prev,
            [sectionToExpand]: true
          }));
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, activeSection]);

  // Scroll active section into view in the TOC
  useEffect(() => {
    if (activeSectionRef.current && tocRef.current) {
      const tocRect = tocRef.current.getBoundingClientRect();
      const activeRect = activeSectionRef.current.getBoundingClientRect();
      
      if (activeRect.top < tocRect.top || activeRect.bottom > tocRect.bottom) {
        activeSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [activeSection]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      
      // Close drawer on mobile after navigation
      if (isMobile) {
        setDrawerOpen(false);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpand = (id, event) => {
    event.stopPropagation();
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleBookmark = (id, event) => {
    event.stopPropagation();
    setBookmarkedSections(prev => {
      if (prev.includes(id)) {
        return prev.filter(sectionId => sectionId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const shareSection = (id, event) => {
    event.stopPropagation();
    const url = new URL(window.location.href);
    url.hash = id;
    
    if (navigator.share) {
      navigator.share({
        title: 'Shared section',
        url: url.toString()
      });
    } else {
      navigator.clipboard.writeText(url.toString());
      // You would add a snackbar notification here
      alert('Link copied to clipboard');
    }
  };

  const getReadingTime = (section) => {
    // This is a placeholder. In a real implementation, you would calculate
    // based on the content length of each section
    return Math.floor(Math.random() * 5) + 1;
  };

  const getProgressColor = (sectionId) => {
    if (readSections.includes(sectionId)) {
      return theme.palette.success.main;
    }
    if (sectionId === activeSection) {
      return theme.palette.primary.main;
    }
    return theme.palette.grey[400];
  };

  const filterSections = (sections) => {
    if (!searchTerm) return sections;
    
    return sections.filter(section => {
      const matchesTitle = section.title.toLowerCase().includes(searchTerm.toLowerCase());
      
      let hasMatchingSubsections = false;
      if (section.subsections) {
        const filteredSubsections = section.subsections.filter(subsection => {
          const subMatchesTitle = subsection.title.toLowerCase().includes(searchTerm.toLowerCase());
          
          let hasMatchingItems = false;
          if (subsection.items) {
            hasMatchingItems = subsection.items.some(item => 
              item.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          return subMatchesTitle || hasMatchingItems;
        });
        
        hasMatchingSubsections = filteredSubsections.length > 0;
      }
      
      return matchesTitle || hasMatchingSubsections;
    });
  };

  const renderSectionItem = (section, level = 0, parentExpanded = true) => {
    const isActive = activeSection === section.id;
    const isExpanded = expandedSections[section.id];
    const isBookmarked = bookmarkedSections.includes(section.id);
    const isRead = readSections.includes(section.id);
    const hasSubsections = section.subsections && section.subsections.length > 0;
    const readingTime = showReadingTime ? getReadingTime(section) : null;
    
    const indentPadding = level * 16;
    
    return (
      <Box key={section.id}>
        <Box
          ref={isActive ? activeSectionRef : null}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: `${indentPadding}px`,
            pr: 1,
            py: 0.75,
            borderRadius: 1,
            mb: 0.5,
            cursor: 'pointer',
            position: 'relative',
            bgcolor: isActive 
              ? alpha(theme.palette.primary.main, 0.1)
              : 'transparent',
            '&:hover': {
              bgcolor: isActive 
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.action.hover, 0.1),
            },
            transition: 'all 0.2s ease',
            ...(isActive && {
              borderLeft: `3px solid ${theme.palette.primary.main}`,
              pl: `${indentPadding - 3}px`,
            })
          }}
          onClick={() => scrollToSection(section.id)}
        >
          {/* Status indicator */}
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            {isRead ? (
              <CheckCircleIcon 
                fontSize="small" 
                sx={{ 
                  color: theme.palette.success.main,
                  fontSize: '0.8rem'
                }} 
              />
            ) : isActive ? (
              <RadioButtonUncheckedIcon 
                fontSize="small" 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontSize: '0.8rem',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.7 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.7 }
                  }
                }} 
              />
            ) : (
              <RadioButtonUncheckedIcon 
                fontSize="small" 
                sx={{ 
                  color: theme.palette.text.disabled,
                  fontSize: '0.8rem'
                }} 
              />
            )}
          </Box>
          
          {/* Section title */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: isActive ? 'bold' : 'normal',
              color: isActive 
                ? theme.palette.primary.main 
                : isRead
                  ? theme.palette.text.secondary
                  : theme.palette.text.primary,
              flexGrow: 1,
              transition: 'color 0.2s ease',
            }}
          >
            {section.title}
            {section.isNew && (
              <Chip 
                label="New" 
                size="small" 
                color="primary" 
                sx={{ 
                  ml: 1, 
                  height: 16, 
                  fontSize: '0.6rem',
                  boxShadow: theme.palette.mode === 'dark' ? '0 0 5px rgba(33, 150, 243, 0.5)' : 'none'
                }} 
              />
            )}
          </Typography>
          
          {/* Reading time */}
          {readingTime && (
            <Tooltip title="Estimated reading time">
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.text.secondary,
                mr: 1,
                fontSize: '0.75rem'
              }}>
                <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                {readingTime}m
              </Box>
            </Tooltip>
          )}
          
          {/* Action buttons */}
          {showBookmarks && (
            <Tooltip title={isBookmarked ? "Remove bookmark" : "Bookmark this section"}>
              <IconButton 
                size="small" 
                onClick={(e) => toggleBookmark(section.id, e)}
                sx={{ 
                  opacity: isBookmarked ? 1 : 0.3,
                  '&:hover': { opacity: 1 },
                  transition: 'opacity 0.2s ease',
                  mr: 0.5
                }}
              >
                {isBookmarked ? (
                  <BookmarkIcon fontSize="small" color="primary" />
                ) : (
                  <BookmarkBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Share this section">
            <IconButton 
              size="small" 
              onClick={(e) => shareSection(section.id, e)}
              sx={{ 
                opacity: 0.3,
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.2s ease',
                mr: hasSubsections ? 0.5 : 0
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
                   {/* Expand/collapse button for sections with subsections */}
                   {hasSubsections && allowCollapse && (
            <Tooltip title={isExpanded ? "Collapse section" : "Expand section"}>
              <IconButton 
                size="small" 
                onClick={(e) => toggleExpand(section.id, e)}
                sx={{ 
                  transition: 'transform 0.2s ease',
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {/* Progress indicator */}
        {showProgress && (
          <Box 
            sx={{ 
              height: 2, 
              width: '100%', 
              mb: 0.5,
              bgcolor: alpha(theme.palette.grey[500], 0.2),
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box 
              sx={{ 
                height: '100%', 
                width: isRead ? '100%' : isActive ? '50%' : '0%',
                bgcolor: getProgressColor(section.id),
                transition: 'width 0.5s ease, background-color 0.5s ease',
                borderRadius: 1
              }} 
            />
          </Box>
        )}
        
        {/* Render subsections if expanded */}
        {hasSubsections && (
          <Collapse in={isExpanded && parentExpanded}>
            <Box>
              {section.subsections.map(subsection => 
                renderSectionItem(subsection, level + 1, isExpanded && parentExpanded)
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  // Main render
  const filteredSections = filterSections(sections);
  
  // Mobile drawer version
  const tocContent = (
    <Box>
      {/* Header with search */}
      <Box sx={{ 
        p: 2, 
        position: 'sticky', 
        top: 0, 
        bgcolor: theme.palette.background.paper,
        zIndex: 1,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ListAltIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            Contents
          </Typography>
          {isMobile && (
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        
        {showSearch && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ mb: 1 }}
          />
        )}
        
        {/* Overall progress */}
        {showProgress && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Reading Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overallProgress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={overallProgress} 
              sx={{ 
                height: 6, 
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1)
              }} 
            />
          </Box>
        )}
        
        {/* Filter options */}
        {showBookmarks && bookmarkedSections.length > 0 && (
          <Box sx={{ display: 'flex', mt: 1 }}>
            <Chip
              icon={<BookmarkIcon fontSize="small" />}
              label="Bookmarks"
              size="small"
              color={searchTerm === '__bookmarks__' ? 'primary' : 'default'}
              onClick={() => setSearchTerm(prev => prev === '__bookmarks__' ? '' : '__bookmarks__')}
              sx={{ mr: 1 }}
            />
            <Chip
              icon={<FilterListIcon fontSize="small" />}
              label="All Sections"
              size="small"
              color={searchTerm === '' ? 'primary' : 'default'}
              onClick={() => setSearchTerm('')}
            />
          </Box>
        )}
      </Box>
      
      {/* Section list */}
      <Box 
        ref={tocRef}
        sx={{ 
          p: 2,
          pt: 1,
          maxHeight: isMobile ? 'calc(100vh - 180px)' : maxHeight,
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {searchTerm === '__bookmarks__' ? (
          bookmarkedSections.length > 0 ? (
            sections
              .flatMap(section => {
                const matches = [];
                if (bookmarkedSections.includes(section.id)) {
                  matches.push(section);
                }
                
                if (section.subsections) {
                  section.subsections.forEach(subsection => {
                    if (bookmarkedSections.includes(subsection.id)) {
                      matches.push(subsection);
                    }
                    
                    if (subsection.items) {
                      subsection.items.forEach(item => {
                        if (bookmarkedSections.includes(item.id)) {
                          matches.push(item);
                        }
                      });
                    }
                  });
                }
                
                return matches;
              })
              .map(section => renderSectionItem(section, 0, true))
          ) : (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No bookmarked sections yet
            </Typography>
          )
        ) : filteredSections.length > 0 ? (
          filteredSections.map(section => renderSectionItem(section, 0, true))
        ) : (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No matching sections found
          </Typography>
        )}
      </Box>
      
      {/* Footer actions */}
      <Box sx={{ 
        p: 2, 
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Tooltip title="Scroll to top">
          <IconButton size="small" onClick={scrollToTop}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Box>
          <Badge 
            badgeContent={readSections.length} 
            color="success"
            max={99}
            sx={{ mr: 1 }}
          >
            <Tooltip title="Sections read">
              <CheckCircleIcon fontSize="small" color="action" />
            </Tooltip>
          </Badge>
          
          <Badge 
            badgeContent={bookmarkedSections.length} 
            color="primary"
            max={99}
          >
            <Tooltip title="Bookmarked sections">
              <BookmarkIcon fontSize="small" color="action" />
            </Tooltip>
          </Badge>
        </Box>
      </Box>
    </Box>
  );
  
  // Floating button for mobile
  const floatingButton = isMobile && showFloatingButton && !drawerOpen && (
    <Fab
      color="primary"
      size="small"
      aria-label="table of contents"
      onClick={() => setDrawerOpen(true)}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        boxShadow: theme.shadows[4]
      }}
    >
      <MenuIcon />
    </Fab>
  );
  
  return (
    <>
      {isMobile ? (
        <>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: { 
                width: isSmall ? '100%' : '320px',
                maxWidth: '100%'
              }
            }}
          >
            {tocContent}
          </Drawer>
          {floatingButton}
        </>
      ) : (
        <Paper 
          elevation={3} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[6]
            }
          }}
        >
          {tocContent}
        </Paper>
      )}
    </>
  );
};

export default TableOfContents;